import SectionCard, {
  SectionCardGroup,
  SectionCardControls
} from "../../../../Composites/SectionCard/SectionCard";
import { List, Button, Select, Text, Switch, Flex, isNullish } from "@theorgchartdev/storybook";
import React, { useEffect, useState } from "react";
import cx from "classnames";
import RenamePopover from "../../../../Composites/RenamePopover/RenamePopover";
import * as StateLib from "@/Utils/StateLib";
import { useParams } from "react-router-dom";
import { QUIConfig } from "@coreapp/CoreApp/QUIConfig";
import { enumFeaturesList, kUIProfileOptions } from "@coreapp/constants";
import { appstr } from "@coreapp/utils/appstr";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import UpgradePopover from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradePopover";
import { getFeatureAccess } from "@/Utils/Misc";
import { gPremiumAppLink } from "@coreapp/whitelabel/brand";

const supportLoginOnlyOptions = [
  kUIProfileOptions.fPackaging.command,
  kUIProfileOptions.fHasSettings.command,
  kUIProfileOptions.fHasNavigation.command
];

const ValidOption = cmd => {
  const { isSupportLogin } = usePermissionCheck();
  if (supportLoginOnlyOptions.includes(cmd)) return isSupportLogin;
  //other criteria

  return true;
};

const currentProfileValue = (profile, key, property) => {
  if (isNullish(profile[key])) {
    var defaultConfig = new QUIConfig();
    return defaultConfig[property];
  } else {
    return profile[key];
  }
};

const showUpgradeUI = (showPricingAndPackaging, feature) => {
  if (showPricingAndPackaging && !getFeatureAccess(feature)) {
    return false;
  }
  return true;
};

const getUpgradePopoverProps = (optionKey, showPricingAndPackaging) => {
  if (
    optionKey === "showGrouping" &&
    !showUpgradeUI(showPricingAndPackaging, enumFeaturesList.grouping)
  ) {
    return {
      pillLabel: appstr("Upgrade to group"),
      popoverText: {
        message: appstr("grouping message"),
        template: appstr("grouping template"),
        formatConfig: {
          bold1: { type: "bold" }
        },
        replaceDict: {
          bold1: appstr("compact and concise")
        }
      },
      featureKey: enumFeaturesList.grouping
    };
  }

  if (
    optionKey === "planning" &&
    !showUpgradeUI(showPricingAndPackaging, enumFeaturesList.planning)
  ) {
    return {
      pillLabel: appstr("Upgrade to plan"),
      popoverText: {
        message: appstr("planning upgrade message"),
        template: appstr("planning upgrade message"),
        formatConfig: {
          bold1: { type: "bold" }
        },
        replaceDict: {}
      },
      featureKey: enumFeaturesList.planning
    };
  }

  if (
    optionKey === "positionMgmt" &&
    !showUpgradeUI(showPricingAndPackaging, enumFeaturesList.positionManagement)
  ) {
    return {
      pillLabel: appstr("Upgrade"),
      popoverText: {
        message: appstr(
          "Position Management enables you to visualize your employees and positions separately to gain better insight in your open roles and key position details"
        ),
        template: appstr(
          "Position Management enables you to visualize your employees and positions separately to gain better insight in your open roles and key position details"
        ),
        formatConfig: {
          bold1: { type: "bold" }
        },
        replaceDict: {}
      },
      featureKey: enumFeaturesList.positionManagement
    };
  }

  return null;
};

const BuildUIProfileSettings = (theProfile, onChange, showPricingAndPackaging) => {
  const profileOptions = [];
  const baseClass = "ocn-field-toggle-option";

  for (let UIProfileOptionItem in kUIProfileOptions) {
    const theOption = kUIProfileOptions[UIProfileOptionItem];
    let optionKey = theOption.command;
    let checked = currentProfileValue(theProfile, optionKey, UIProfileOptionItem);
    let disabledcheck = false;
    let pillLabel = "";
    let upgradeText = {};

    if (!ValidOption(optionKey)) continue;

    const upgradeProps = getUpgradePopoverProps(optionKey, showPricingAndPackaging);
    const isDisabled = upgradeProps !== null;
    checked = isDisabled ? false : checked;
    const checkedClass = checked && `${baseClass}-label--checked`;

    const profileOption = {
      content: (
        <span className={baseClass}>
          <Flex direction="row" gap={"8px"}>
            <span className={cx(`${baseClass}-label`, checkedClass)}>
              <Text size={"medium"}>{appstr(theOption["string"])}</Text>
            </span>
            {upgradeProps && <UpgradePopover {...upgradeProps} />}
          </Flex>
          <span className={`${baseClass}-switch`}>
            <Switch
              id={"ui-profile-fields-" + UIProfileOptionItem}
              data-testid={`account-settings-ui-profiles-setting-${UIProfileOptionItem}-switch`}
              onChange={() => onChange({ value: optionKey, property: UIProfileOptionItem })}
              checked={checked}
              disabled={isDisabled}
            />
          </span>
        </span>
      ),
      clickHandler: isDisabled
        ? undefined
        : () => onChange({ value: optionKey, property: UIProfileOptionItem }),
      divider: true,
      value: UIProfileOptionItem
    };
    profileOptions.push(profileOption);
  }
  return profileOptions;
};

export const UIProfiles = ({ accountConfig, handleUpdate }) => {
  const { id } = useParams();
  const { showPricingAndPackaging } = usePermissionCheck();

  const defaultProfileID = "default";
  const [curProfileID, setCurProfileID] = useState(defaultProfileID);

  useEffect(() => {
    const wsMgr = StateLib.GetWSMgr();
    const eventTracker = wsMgr.GetUsageTracker();
    eventTracker.ReportEvent("account_settings.ui_profiles.opened");
  }, []);

  if (!accountConfig) return;

  const accountConfigProfiles = accountConfig?.["uiprofiles"];
  const curProfile = accountConfigProfiles[curProfileID];

  const validateProfileName = (newName, curProfAllowed = true) => {
    if (newName.indexOf(" ") > -1)
      return { success: false, errorResponse: appstr("nospaceuiprofile") };
    if (!curProfAllowed && newName === curProfileID) {
      return { success: false };
    }
    let existingProfile = Object.keys(accountConfigProfiles).includes(newName);
    const success = !existingProfile;
    return { success: success, errorResponse: success ? null : appstr("duplicateuiprofile") };
  };

  const updateProfiles = (mode, data) => {
    let updatedProfilesObj = { ...accountConfigProfiles };
    switch (mode) {
      case "rename":
        const renamedProfile = JSON.parse(JSON.stringify(accountConfigProfiles[curProfileID]));
        updatedProfilesObj[data] = renamedProfile;
        delete updatedProfilesObj[curProfileID];
        setCurProfileID(data);
        break;
      case "delete":
        delete updatedProfilesObj[curProfileID];
        setCurProfileID(defaultProfileID);
        break;
      case "copy":
      case "create":
        const nameRoot = mode == "copy" ? curProfileID : data;
        let newName = nameRoot;
        let counter = 1;
        while (updatedProfilesObj[newName]) newName = nameRoot + (counter++).toString();

        const copiedProfile = JSON.parse(JSON.stringify(updatedProfilesObj[curProfileID]));
        updatedProfilesObj[newName] = copiedProfile;
        setCurProfileID(newName);
        break;
      default:
        break;
    }
    handleUpdate({ uiprofiles: updatedProfilesObj }, "uiprofiles");
  };

  const onSettingToggle = ({ value, property }) => {
    const currentValue = currentProfileValue(curProfile, value, property);
    curProfile[value] = !currentValue;
    accountConfig["uiprofiles"][curProfileID] = curProfile;
    handleUpdate({ uiprofiles: accountConfig["uiprofiles"] }, "uiprofiles");
  };

  const setProfile = e => {
    setCurProfileID(e.value);
  };

  const buildProfileOptions = (uiProfiles, clickHandler) => {
    let optionList = [];

    for (let uiProfileID in uiProfiles) {
      let option = {};

      option.label = uiProfileID;
      option.value = uiProfileID;
      option.clickHandler = clickHandler;
      let icon = null;

      if (uiProfileID == "default") {
        icon = "star-regular";
      }

      if (icon) {
        option.icon = icon;
      }

      optionList.push(option);
    }
    return optionList;
  };

  const renameTrigger = (
    <Button
      dataTestId={"ui-profile-rename"}
      modifier={"secondary"}
      iconProps={{
        name: "pen"
      }}
      disabled={defaultProfileID === curProfileID}
    >
      {appstr("Rename profile")}
    </Button>
  );

  const createTrigger = (
    <Button
      dataTestId={"ui-profile-create"}
      modifier={"secondary"}
      className={"oc-special-secondary"}
      iconProps={{
        name: "plus"
      }}
    >
      {appstr("createuiprofile")}
    </Button>
  );

  return (
    <SectionCardGroup>
      <SectionCardControls>
        <Flex gap={"12px"}>
          <Flex width={"300px"}>
            <Select
              dataTestId={"ui-profile-select"}
              selectSize={"medium"}
              optionSize={"medium"}
              options={buildProfileOptions(accountConfigProfiles, setProfile)}
              listWidth={"medium"}
              label={curProfileID}
              value={curProfileID}
            />
          </Flex>
          <RenamePopover
            name={curProfileID}
            onCommit={newName => updateProfiles("rename", newName)}
            validateFxn={validateProfileName}
            popoverProps={{ reference: renameTrigger }}
            inputProps={{
              id: "ui-profile-rename-input",
              dataTestId: "account-settings-ui-profiles-rename-input"
            }}
            triggerTestId="account-settings-ui-profiles-rename-popover-trigger"
            confirmBtnTestId="account-settings-ui-profiles-rename-confirm-btn"
          />
          <Button
            dataTestId={"ui-profile-copy"}
            onClick={() => updateProfiles("copy")}
            modifier={"secondary"}
            iconProps={{
              name: "copy"
            }}
          >
            {appstr("copyuiprofile")}{" "}
          </Button>
          <Button
            dataTestId={"ui-profile-delete"}
            onClick={() => updateProfiles("delete")}
            disabled={defaultProfileID === curProfileID}
            modifier={"secondary"}
            iconProps={{
              name: "trash"
            }}
          >
            {appstr("Delete") + " " + appstr("Profile")}
          </Button>
        </Flex>
        <Flex className={"oc-right-justify"}>
          <RenamePopover
            label={`${appstr("Name")} ${appstr("Profile")}`}
            name={appstr("Untitled")}
            popoverProps={{ reference: createTrigger }}
            onCommit={newName => updateProfiles("create", newName)}
            validateFxn={validateProfileName}
            inputProps={{
              id: "ui-profile-create-input",
              dataTestId: "account-settings-ui-profiles-create-input"
            }}
            triggerTestId="account-settings-ui-profiles-create-popover-trigger"
            confirmBtnTestId="account-settings-ui-profiles-create-confirm-btn"
          />
        </Flex>
      </SectionCardControls>
      <SectionCard title={curProfileID}>
        <div>
          <List
            enableVirtualization={false}
            options={BuildUIProfileSettings(curProfile, onSettingToggle, showPricingAndPackaging)}
            listWidth="fill"
            optionSize={"large"}
            showCheck={true}
            value={null}
          />
        </div>
      </SectionCard>
    </SectionCardGroup>
  );
};
